// <SituationCard> — canonical ClientHub kit component (Epic 1 · S1.2).
// Two surfaces share it:
//  - Hub (Now): passes `severity` → a meaning-tinted surface (warm/cool) with a
//    leading severity dot (print/greyscale-safe). Severity contrast IS the row
//    hierarchy: a warm rank-1 row reads visibly heavier than a cool rank-6 row.
//  - Effort: passes `tone` ('human'|'agent') → a plain or violet-railed surface
//    so a human entry and an agent run read as visually distinct in one list.
import React from "react";
import { severityToTint, tintClasses, railClasses } from "../../shared/tints";

const TONES = {
  human: "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800",
  // Agent runs sit in the same list but read distinct: a violet left rail + tint
  // (violet is not amber — amber is reserved for the anomaly/warning affordance,
  // so the two never collide).
  agent:
    "bg-violet-50/60 dark:bg-violet-900/10 border-gray-200 dark:border-gray-800 border-l-4 border-l-violet-400 dark:border-l-violet-600",
};

/**
 * @param {object} props
 * @param {"critical"|"warn"|"watch"|string} [props.severity]  Hub rows.
 * @param {"human"|"agent"} [props.tone]                       Effort rows.
 * @param {boolean} [props.flat]  Plain surface + severity dot (Now rows are ordinary
 *   surfaces; the takeaway is the only glass). Omit to tint the whole row by severity.
 * @param {boolean} [props.bare]  Skip the tone card shell (rounded/border/padding/
 *   surface tint) so the caller fully owns the row presentation via `className`.
 *   Default false keeps the card look. Used by Work inbox rows, which are dense list
 *   rows with bottom-border separators — not cards. Only honoured by the `tone` branch.
 * @param {"warm"|"warn"|"waiting"|"positive"|"neutral"|string} [props.rail]  Flat list-row
 *   variant for rows that sit INSIDE a card: a 4px meaning-tinted left edge on a quiet
 *   fill, no border box, no dot. Takes precedence over `severity` and `tone`.
 * @param {Record<string,string|number>} [props.dataAttrs]     Spread onto the root.
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 */
export default function SituationCard({
  severity,
  tone,
  rail,
  flat = false,
  bare = false,
  dataAttrs = {},
  children,
  className = "",
  ...rest
}) {
  if (rail) {
    return (
      <div
        {...dataAttrs}
        className={`border-l-4 ${railClasses(rail)} rounded-lg bg-gray-50 dark:bg-gray-800/50 px-4 py-4 ${className}`.trim()}
        {...rest}
      >
        {children}
      </div>
    );
  }

  if (tone) {
    // `bare` skips the card shell (rounded/border/p-3/surface tint) so the caller
    // owns the row via `className` — Work inbox rows are dense list rows with a
    // bottom-border separator, not cards. Default keeps the card look for Effort.
    const shell = bare ? "" : `rounded-lg border p-3 ${TONES[tone] || TONES.human}`;
    return (
      <div
        className={`${shell} ${className}`.trim()}
        {...rest}
      >
        {children}
      </div>
    );
  }

  const t = tintClasses(severityToTint(severity));
  // A flat row keeps a plain surface — the severity dot carries urgency while the card
  // stays calm, so the single glass takeaway above the feed is the one thing that reads
  // as heavy. Non-flat rows tint the whole surface by severity (ClientHub, Work, Threads).
  const surface = flat ? tintClasses("cool").surface : t.surface;
  return (
    <div
      {...dataAttrs}
      className={`flex items-start gap-3 rounded-xl border ${surface} px-4 py-3 md:px-5 ${className}`}
    >
      <span
        className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${t.dot}`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
